/* Whether a booking's own start is a start the technician could have made.
 *
 * A square seated off the book keeps the BOOKING's start, not the moment it was
 * put up — a client booked at ten and seated at ten past is ten minutes into a
 * thirty minute service, not starting one, and that is what makes the square's
 * countdown agree with the appointment behind it. See `bookedOutcome`.
 *
 * That backdating is right whenever the technician was free at the booked time
 * and somebody was simply slow to the keyboard. It is wrong — and wrong in the
 * one direction that matters — when they were not free, because the client
 * before them ran long. Ten o'clock is then a time this person was
 * demonstrably still working on somebody else, and starting the new square
 * there hands them a countdown that is already twenty minutes gone, a
 * timer that goes red before the client has sat down, and a length in History
 * that is the salon's lateness rather than the technician's work.
 *
 * Nothing on the board can pick between those two. Both look identical from the
 * outside: a booking whose time has passed, going up now. The desk knows which
 * one it is because they watched it happen. So the answer is asked — but only
 * when there is something to ask about, which is what this file decides.
 *
 * ── WHY THIS IS A RULES FILE ─────────────────────────────────────────────────
 *
 * It is a comparison of four timestamps, and getting it wrong is quiet in both
 * directions: too eager and the desk is asked a question on every ordinary
 * seating until they stop reading it, too shy and the overrun case silently
 * backdates the way it always did. Neither shows up as a broken screen. Both
 * are assertable with four numbers and no DOM, no clock and no Supabase, the
 * same reason js/rules/turns.js and js/rules/waitlist.js exist. This file
 * imports nothing.
 */

/* How far past a time something has to be before it counts as being past it.
 *
 * A service that ran a minute over did not stop anybody starting the next one,
 * and a booking whose start is a minute behind the technician coming free is
 * the ordinary case of somebody reaching the keyboard. Below this the two
 * answers are the same answer, and a sheet that asks anyway is a sheet that
 * gets clicked through.
 */
export var OVERRUN_GRACE_MS = 60000;

/* The service this technician came off, or null.
 *
 * By finish time rather than by position in the column: a square recorded after
 * the fact lands where it happened, and the one at the bottom of the column is
 * not always the last one to end. What is being asked is "when did this person
 * last come free", and only the clock answers that.
 *
 * A turn still running is not an answer. It is not over, so it cannot have run
 * over — and a technician mid-service cannot be given a new square at all; see
 * `bookTurn`.
 */
export function lastFinished(turns) {
  var best = null;
  (turns || []).forEach(function (t) {
    if (!t || t.endedAt == null) return;
    if (!best || t.endedAt > best.endedAt) best = t;
  });
  return best;
}

/* The question, or null for "there isn't one".
 *
 * `prevEndsAt` is when the previous square was DUE to be over — the booking's
 * own length behind it, or the turn type's usual. Null means nothing on the
 * board says when it should have ended, which is not the same as "it was on
 * time": with no scheduled end there is no such thing as running over, and the
 * booking's start stands as it always has.
 *
 * Three things all have to be true, and the third is the one that keeps this
 * quiet. The previous service ran past its own time; the booking's time has
 * already passed, so there is a backdating to argue with at all; and the
 * overrun actually swallowed the booked start — a technician who ran twenty
 * minutes over an eleven o'clock service and is seating a two o'clock booking
 * was free the whole time, and being asked about it teaches the desk to stop
 * reading the sheet.
 */
export function lateStart(prev, prevEndsAt, bookedStartsAt, now) {
  if (!prev || prev.endedAt == null) return null;
  if (!prevEndsAt) return null;
  // A booking whose time has not come starts now whichever answer is given, so
  // there is nothing between them to choose.
  if (!bookedStartsAt || bookedStartsAt >= now) return null;

  var over = prev.endedAt - prevEndsAt;
  if (over <= OVERRUN_GRACE_MS) return null;

  var late = prev.endedAt - bookedStartsAt;
  if (late <= OVERRUN_GRACE_MS) return null;

  return {
    bookedAt: bookedStartsAt,   // what the book says this was to start
    freedAt: prev.endedAt,      // when this technician actually came free
    over: over,                 // how far past its own time the last one ran
    late: late                  // how long after the booked start that was
  };
}
